import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { message } from 'antd';
import { useLoginService } from 'service/login';
import { useStaffContext } from 'store';

const homeRoute = '/moniter';

const Login = () => {
    const { setIdToken } = useStaffContext();
    const { postLogin, postSmsCode, getIsTokenValid, storageKeys } = useLoginService();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const navigate = useNavigate();

    const usernameRef = useRef(null);
    const passwordRef = useRef(null);
    const smsCodeRef = useRef(null);
    const loginRef = useRef(null);

    const [token, setToken] = useState('');
    const [userId, setUserId] = useState(0);
    const [cityId, setCityId] = useState('');
    const [isSmsContentDisplay, setIsSmsContentDisplay] = useState(false);
    const [isLoginBtnDisabled, setIsLoginBtnDisabled] = useState(false);

    useEffect(() => {
        const storedIdToken = sessionStorage.getItem(storageKeys.idToken);
        const isTokenValid = getIsTokenValid(storedIdToken);
        if (isTokenValid) {
            setIdToken(storedIdToken);
            navigate(homeRoute);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loginSuccessHandler = (_token, _cityId) => {
        message.success('登入成功！');
        setIdToken(_token);
        sessionStorage.setItem(storageKeys.idToken, _token);
        sessionStorage.setItem(storageKeys.city, _cityId);
        navigate(homeRoute);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        // 代表已經按過登入了，不可重複按
        if (loginRef.current) {
            return;
        }

        try {
            const username = usernameRef.current.value;
            const password = passwordRef.current.value;
            if (!username || username === '' || !password || password === '') {
                throw new Error('帳號或密碼為必填');
            }

            loginRef.current = true;
            setIsLoginBtnDisabled(true);

            try {
                const recaptchaToken = await executeRecaptcha?.('login') || '';
                const loginRes = await postLogin(recaptchaToken, { account: username, password });
                loginRef.current = true;
                setToken(loginRes.token);
                setUserId(loginRes.id);
                setCityId(loginRes.cityId);

                // 假設簡訊驗證在一個月內沒有通過，則跳到下一步
                if (loginRes.smsPassed === false) {
                    setIsSmsContentDisplay(true);
                    smsCodeRef.current.value = '';
                } else {
                    loginSuccessHandler(loginRes.token, loginRes.cityId);
                }
            } catch (err) {
                throw new Error('帳號或密碼錯誤');
            }
        } catch (err) {
            message.error(err.message);
            loginRef.current = false;
            setIsLoginBtnDisabled(false);
        }
    };

    const onSubmitSmsCode = async (e) => {
        e.preventDefault();

        try {
            const smsCode = smsCodeRef.current.value;
            if (smsCode === null || smsCode === '') {
                throw new Error('簡訊驗證碼為必填');
            }

            try {
                // 需要帶 Authorization Token
                await postSmsCode({
                    id: userId,
                    token,
                    smsCode
                });

                loginSuccessHandler(token, cityId);
            } catch (err) {
                throw new Error('簡訊驗證碼錯誤');
            }
        } catch (err) {
            message.error(err.message);
        }
    };

    const loginJsx = (
        <>
            <h1 className="text-primary text-2xl font-bold">URiDE</h1>
            <h2 className="text-primary text-xl mb-6">政府查詢平台登入</h2>
            <form className="flex flex-col w-[250px]" onSubmit={onSubmit}>
                <input
                    ref={usernameRef}
                    placeholder="帳號"
                    className="text-primary py-1 px-2 mb-3 rounded-md border border-primary"
                />
                <input
                    ref={passwordRef}
                    placeholder="密碼"
                    type="password"
                    className="py-1 px-2 mb-3 rounded-md border border-primary"
                />
                <button
                    type="submit"
                    className="
                        bg-[#FDD01A] text-primary py-2 font-bold rounded-md
                        disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed"
                    disabled={isLoginBtnDisabled}
                >
                    登入
                </button>
            </form>
        </>
    );

    const smsVerifyJsx = (
        <>
            <h2 className="text-primary text-xl mb-6 font-bold">手機號碼認證</h2>
            <p className="text-primary mb-6 w-[250px]">已將驗證碼寄至您的手機，請輸入簡訊驗證碼：（六碼數字）</p>
            <form className="flex flex-col w-[250px]" onSubmit={onSubmitSmsCode}>
                <input
                    ref={smsCodeRef}
                    placeholder="簡訊驗證碼"
                    className="py-1 px-2 mb-3 rounded-md border border-primary"
                />
                <button
                    type="submit"
                    className="bg-[#FDD01A] text-primary py-2 font-bold rounded-md"
                >
                    確認
                </button>
            </form>
        </>
    );

    return (
        <div className="login-container flex items-center justify-center">
            <div className="bg-white/90 inline-flex flex-col rounded-md px-5 pt-9 pb-5 text-center shadow-lg">
                {isSmsContentDisplay ? smsVerifyJsx : loginJsx}
            </div>
        </div>
    );
};

export default Login;
